// TS twin of iOS `AssemblerService.swift`.
//
// Polls the assembler API until the USDZ is ready, then returns the URL
// plus an optional WheelDetails projection. Mirrors:
// - 500ms poll interval, 15s timeout (configurable for tests)
// - 404 = pending → keep polling
// - non-200/non-404 = invalid_response
// - fetch throws = network_error
// - JSON parse failure = decoding_error
// - status='complete' + usdz URL = return PollResult
// - DTO → WheelDetails projection via translateBasePart (pinned in
//   fixtures/assembler-dto-projection.json)
//
// The smart-retry BUDGET (b08a345 — max-5 retries, skip-poll-if-cached,
// cancel-in-flight) lives at the controller layer where the user-facing
// Retry button state is owned. See task D (wheel-placement-controller).
//
// fetch is injectable via constructor — production passes the platform
// fetch; tests pass an in-memory scripted implementation.

import { atoLog } from '@/services/ato-log'
import { assemblerBaseURL, type AppEnvironment } from '@/models/app-environment'
import { type AssemblyCode } from '@/models/assembly-code'
import { wheelSize, type WheelSize } from '@/models/wheel-size'
import { wheelDetails, type WheelDetails } from '@/models/wheel-details'

// ── Wire-format DTOs (mirror assembler JSON; never escape the service) ──

export interface AssemblerBasePart {
  id?: number
  display_name?: string
  brand?: { display_name?: string; logo?: string; logo_dark?: string | null }
  meta_values?: {
    size?: string
    /** The catalog's LIVE size key. `size` ("24x12") stopped being
     *  authored long ago; today's wheels carry `diameter` ("17") —
     *  verified against staging 2026-09-07: zero LIVE wheels with
     *  size, dozens with diameter. */
    diameter?: string
    available_properties?: {
      size?: Array<{
        property_value_id?: number
        value_slug?: string
        value_display_name?: string
      }>
      /** The live size list ("20", "18", …) — `size` is the legacy key. */
      diameter?: Array<{
        property_value_id?: number
        value_slug?: string
        value_display_name?: string
      }>
    }
    /** ADR-104 — 'offered' when the diameter list is the sizes the wheel is
     *  SOLD in (it may include sizes no mesh exists for). */
    available_properties_source?: { diameter?: string }
  }
}

interface AssemblerResponse {
  status: string
  urls: { glb?: string; usdz?: string }
  base_part?: AssemblerBasePart
}

// ── Error ───────────────────────────────────────────────────────────────

export type AssemblerErrorKind =
  | 'timeout'
  | 'invalid_response'
  | 'decoding_error'
  | 'network_error'
  | 'invalid_url'

interface AssemblerErrorOpts {
  message: string
  httpStatus?: number
  cause?: unknown
}

export class AssemblerError extends Error {
  readonly kind: AssemblerErrorKind
  readonly httpStatus: number | undefined

  constructor(kind: AssemblerErrorKind, opts: AssemblerErrorOpts) {
    super(opts.message, { cause: opts.cause })
    this.name = 'AssemblerError'
    this.kind = kind
    this.httpStatus = opts.httpStatus
  }

  /// Short snake_case label matching iOS AssemblerError.category for unified PostHog dashboards.
  get category(): string {
    return this.kind
  }
}

// ── DTO → domain projection ─────────────────────────────────────────────

export function translateBasePart(basePart: AssemblerBasePart): WheelDetails {
  const sizes: WheelSize[] = []
  // The legacy `size` list wins when present; today's catalog declares the
  // range as `diameter` (2026-10-05, WELD S70: 20, 18, 17, 15).
  const available = basePart.meta_values?.available_properties
  const sizeOptions = available?.size?.length ? available.size : (available?.diameter ?? [])
  for (const opt of sizeOptions) {
    if (opt.value_display_name == null) continue
    const parsed = wheelSize(opt.value_display_name)
    if (parsed != null) sizes.push(parsed)
  }

  const parseURL = (value: string | null | undefined): URL | null => {
    if (value == null) return null
    try {
      return new URL(value)
    } catch {
      return null
    }
  }
  const brandLogoURL = parseURL(basePart.brand?.logo)
  const brandLogoDarkURL = parseURL(basePart.brand?.logo_dark)

  // Fallback chain matches what the catalog actually authors: the
  // legacy `size` ("24x12") wins when present, else `diameter` ("17").
  // Coerced through String() because a payload is never trusted to
  // honour the declared type (DB stores text; a future producer may
  // send a number).
  const rawSize = basePart.meta_values?.size ?? basePart.meta_values?.diameter
  const sizeString = rawSize != null ? String(rawSize) : null
  return wheelDetails({
    partID: basePart.id ?? null,
    brandName: basePart.brand?.display_name ?? 'AutoViz',
    modelName: basePart.display_name ?? 'Custom Wheel',
    brandLogoURL,
    brandLogoDarkURL,
    sizes,
    assembledSize: sizeString != null ? wheelSize(sizeString) : null,
    sizesAreOffered: basePart.meta_values?.available_properties_source?.diameter === 'offered',
  })
}

// ── Service ─────────────────────────────────────────────────────────────

export interface PollResult {
  /**
   * URL of the binary model the renderer will load. On Android WebAR this
   * is the GLB (Three.js GLTFLoader); the iOS twin picks `urls.usdz` from
   * the same upstream response (RealityKit/QuickLook). One field name,
   * platform-specific format choice inside the service.
   */
  readonly modelURL: URL
  readonly details: WheelDetails | null
}

export interface AssemblerServiceConfig {
  fetch?: typeof fetch
  pollIntervalMs?: number
  timeoutMs?: number
}

export interface PollOptions {
  code: AssemblyCode
  env: AppEnvironment
  signal?: AbortSignal
}

type FetchResult =
  | { kind: 'success'; response: AssemblerResponse }
  | { kind: 'pending' }
  | { kind: 'error'; error: AssemblerError }

/**
 * How long to keep polling before calling it.
 *
 * An unknown code answers 404, which is indistinguishable from "not
 * assembled yet" — so an invalid code polls until this budget runs out.
 * Cut 15s → 10s on 2026-08-24 LUAT ("we should bail within 10 seconds
 * of retrying instead of showing 'Loading wheel...' forever"), and put
 * BACK to 15s on 2026-09-16 to match the iOS App Clip. The two clients
 * had disagreed by five seconds for no recorded reason, so Android was
 * failing configurations iOS waited out — and a cold USDZ conversion
 * measured 8.5s on production by itself, before the assembly that
 * precedes it. The 2026-08-24 complaint stands where it applies: an
 * invalid code now spends 15s before it gives up. Hard failures
 * (400/500) still do not wait for it; they throw on the first response.
 */
export const POLL_TIMEOUT_MS = 15_000

export class AssemblerService {
  private readonly fetchImpl: typeof fetch
  private readonly pollIntervalMs: number
  private readonly timeoutMs: number

  constructor(config: AssemblerServiceConfig = {}) {
    this.fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis)
    this.pollIntervalMs = config.pollIntervalMs ?? 500
    this.timeoutMs = config.timeoutMs ?? POLL_TIMEOUT_MS
  }

  async poll(opts: PollOptions): Promise<PollResult> {
    const startTime = Date.now()
    const endpoint = `${assemblerBaseURL(opts.env)}/${opts.code.value}`

    let url: URL
    try {
      url = new URL(endpoint)
    } catch {
      atoLog.event('poll_failed', {
        code: opts.code.value,
        env: opts.env,
        attempts: 0,
        duration_s: 0,
        error_type: 'invalid_url',
      })
      throw new AssemblerError('invalid_url', { message: 'Invalid endpoint URL' })
    }

    atoLog.debug(`🔄 poll start endpoint=${endpoint}`)
    let attempts = 0

    try {
      while (true) {
        throwIfAborted(opts.signal)
        if (Date.now() - startTime >= this.timeoutMs) {
          throw new AssemblerError('timeout', {
            message: 'Request timed out. Please try again.',
          })
        }

        attempts += 1
        const result = await this.fetchStatus(url, opts.signal)

        if (result.kind === 'success') {
          const r = result.response
          atoLog.debug(`📡 poll #${attempts} status=${r.status} glb=${r.urls.glb != null}`)
          // Resolve on the GLB, not on status=='complete'. The worker only
          // writes 'complete' once USDZ conversion succeeds (a step this
          // GLTFLoader-based renderer never consumes); a failed USDZ
          // conversion parks the job at 'glb_complete'/'usdz_pending' behind
          // a 60s retry backoff, which used to time out this poller on a
          // wheel whose GLB had been ready and cached the whole time.
          if (r.urls.glb != null) {
            let modelURL: URL
            try {
              modelURL = new URL(r.urls.glb)
            } catch {
              throw new AssemblerError('invalid_url', {
                message: 'Invalid model URL received.',
              })
            }
            return {
              modelURL,
              details:
                r.base_part != null ? translateBasePart(r.base_part) : null,
            }
          }
          // status != complete OR no glb yet → continue polling
        } else if (result.kind === 'error') {
          throw result.error
        }
        // result.kind === 'pending' → fall through to sleep

        await sleep(this.pollIntervalMs, opts.signal)
      }
    } catch (error) {
      if (isAbortError(error)) throw error
      const e =
        error instanceof AssemblerError
          ? error
          : new AssemblerError('network_error', {
              message: String((error as Error)?.message ?? error),
              cause: error,
            })
      const props: Record<string, unknown> = {
        code: opts.code.value,
        env: opts.env,
        attempts,
        duration_s: (Date.now() - startTime) / 1000,
        error_type: e.category,
      }
      if (e.httpStatus != null) props.http_status = e.httpStatus
      Object.assign(props, atoLog.errorProperties(e))
      atoLog.event('poll_failed', props)
      throw e
    }
  }

  private async fetchStatus(
    url: URL,
    signal: AbortSignal | undefined,
  ): Promise<FetchResult> {
    let response: Response
    try {
      response = await this.fetchImpl(url, { signal })
    } catch (e) {
      if (isAbortError(e)) throw e
      return {
        kind: 'error',
        error: new AssemblerError('network_error', {
          message: String((e as Error)?.message ?? e),
          cause: e,
        }),
      }
    }

    if (response.status === 404) return { kind: 'pending' }
    if (response.status !== 200) {
      return {
        kind: 'error',
        error: new AssemblerError('invalid_response', {
          message: `Invalid response from server (status: ${response.status}).`,
          httpStatus: response.status,
        }),
      }
    }

    try {
      const body = (await response.json()) as AssemblerResponse
      return { kind: 'success', response: body }
    } catch (e) {
      return {
        kind: 'error',
        error: new AssemblerError('decoding_error', {
          message: 'Failed to parse response',
          cause: e,
        }),
      }
    }
  }
}

// ── helpers ─────────────────────────────────────────────────────────────

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError())
      return
    }
    const onAbort = () => {
      clearTimeout(timer)
      reject(abortError())
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw abortError()
}

function abortError(): Error {
  return new DOMException('aborted', 'AbortError')
}

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError'
}
